import { useEffect, useRef } from 'react';

import { type AssignableItem, AssignWizard, AssignWizardHandle } from 'pages/custom_tests_v1/components/assign_wizard';
import { useCustomTestEdit } from 'pages/custom_tests_v1/components/custom_test_edit/context';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useCustomTests } from 'pages/custom_tests_v1/context';
import { useRegisterHeaderControls } from 'components/layout';
import { StudentGroupApi } from 'apis/StudentGroupApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, ListGroup, Row } from 'react-bootstrap';
import { TransactionApi } from 'apis/TransactionApi';
import { useNotification } from 'app/notifications';
import { CustomTestApi } from 'apis/CustomTestApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export const AssignmentsTab = props => {

    const [state, setState] = useSuperState({
        assignedAccessCodes: [],
        assignedClassrooms: [],
        error: null as string,
        loading: true
    });
    const { assignedAccessCodes, assignedClassrooms, loading } = state;

    const { selectedPartner } = useApp();
    const { permissions } = useCustomTests();
    const { name, isOwner, testId } = useCustomTestEdit();
    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const assignWizardRef = useRef<AssignWizardHandle>(null);

    useRegisterHeaderControls(permissions.CanAssign && isOwner ? {
        cta: {
            disabled: loading,
            label: (
                <div className="d-inline-flex align-items-center justify-content-center flex-fill gap-1">
                    <i className="fa-solid fa-plus" />
                    <span>
                        <FormattedMessage defaultMessage="Assign" id="assign.0dabb6f2" />
                    </span>
                </div>
            ),
            onClick: () => launchAssignmentWizard()
        }
    } : null);

    const fetchAssignments = async () => {

        try {

            setState({ loading: true });

            const [transactions, groups, assignedInfo] = await Promise.all([
                TransactionApi.GetAll(),
                StudentGroupApi.GetAll('Partner'),
                CustomTestApi.GetAssignedTransactionsAndGroups(testId, selectedPartner.id)
            ]);

            const assignedAccessCodes: AssignableItem[] = transactions
                .filter(t => assignedInfo.AssignedTransactionIds.includes(t.Id))
                .map(t => ({
                    CreatedDate: t.CreatedDateString,
                    Id: t.Id,
                    Identifier: t.Name,
                    UsesRemaining: t.UsesRemaining ?? 0
                }));

            const assignedClassrooms: AssignableItem[] = groups
                .filter(g => assignedInfo.AssignedStudentGroupIds.includes(g.Id))
                .map(g => ({
                    CreatedDate: g.DateCreate,
                    Description: g.Description ?? '',
                    Id: g.Id,
                    Name: g.Name
                }));

            setState({
                assignedAccessCodes,
                assignedClassrooms,
                loading: false
            });

        } catch (e) {

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const launchAssignmentWizard = async () => {

        const assigned = await assignWizardRef.current?.show({ testId, testName: name });

        if (assigned) {

            createToast(
                formatMessage({ id: 'assignments_saved_successfully.22d8fe61', defaultMessage: 'Assignments saved successfully.' }),
                'success',
                5000
            );

            fetchAssignments();

        }

    };

    useEffect(() => {

        fetchAssignments();

    }, []);

    return (
        <div className={'assignments-tab d-flex flex-column'}>
            {loading ?
                (
                    <div className="d-flex align-items-center justify-content-center p-4">
                        <GMetrixLoader size={'large'} />
                    </div>
                )
                :
                (
                    assignedAccessCodes.length === 0 && assignedClassrooms.length === 0 ?
                        (
                            <em className="text-muted mt-3 text-center p-5 border rounded">
                                <FormattedMessage defaultMessage='No assignments configured. Click "Assign" to assign this test.' id="no_assignments_configured_click_assign_t.f2faa3f2" />
                            </em>
                        )
                        :
                        (
                            <Row>

                                <Col md={6} xs={12}>
                                    <h6 className="mb-2">
                                        <FormattedMessage defaultMessage="Access Codes" id="access_codes.3114ae04" />
                                    </h6>

                                    <ListGroup>
                                        {assignedAccessCodes?.length ?
                                            (
                                                <>
                                                    {assignedAccessCodes.map(accessCode => (
                                                        <ListGroup.Item key={accessCode.Id}>
                                                            {accessCode.Identifier}
                                                        </ListGroup.Item>
                                                    ))}
                                                </>
                                            )
                                            :
                                            (
                                                <ListGroup.Item className={'p-5 d-flex align-items-center justify-content-center'}>
                                                    <span className={'text-muted'}>
                                                        <FormattedMessage defaultMessage="No access codes assigned" id="no_access_codes_assigned.96df64ec" />
                                                    </span>
                                                </ListGroup.Item>
                                            )
                                        }
                                    </ListGroup>
                                </Col>

                                <Col md={6} xs={12}>
                                    <h6 className="mb-2">
                                        <FormattedMessage defaultMessage="Classrooms" id="classrooms.e66138fb" />
                                    </h6>
                                    <ListGroup>
                                        {assignedClassrooms?.length ?
                                            (
                                                <>
                                                    {assignedClassrooms.map(classroom => (
                                                        <ListGroup.Item key={classroom.Id}>
                                                            {classroom.Name}
                                                        </ListGroup.Item>
                                                    ))}
                                                </>
                                            )
                                            :
                                            (
                                                <ListGroup.Item className={'p-5 d-flex align-items-center justify-content-center'}>
                                                    <span className={'text-muted'}>
                                                        <FormattedMessage defaultMessage="No classrooms assigned" id="no_classrooms_assigned.b53cd3bb" />
                                                    </span>
                                                </ListGroup.Item>
                                            )
                                        }
                                    </ListGroup>
                                </Col>

                            </Row>
                        )
                )
            }

            <AssignWizard ref={assignWizardRef} />

        </div>
    );

};
